/*
 * Compact homepage-hero website check. Separate <form>; validates with the same
 * address rules, then runs the same server check in place (progress + results)
 * by reusing the shared runner from WebsiteCheck. No results UI of its own,
 * no storage; analytics events go through the existing gtag/GTM setup.
 */
import { useRef, useState } from "react";
import {
  useWebsiteCheck,
  WebsiteCheckErrorPanel,
  WebsiteCheckProgress,
  WebsiteCheckResults,
} from "@/components/site/WebsiteCheck";

const INVALID_MESSAGE = "Please enter a valid website address, for example yourlawfirm.com.";

/* Same copy the homepage check panel used before it moved into the hero. */
const RESULTS_NOTE =
  "This covers 11 basics. Our full 30+ page audit goes much further, with dozens of additional checks on Google Maps, practice pages, content, links and competitors.";

/** Mirrors the server's address rules (empty, unparsable or dot-less hosts are invalid). */
export function isValidCheckUrl(raw: string): boolean {
  let input = raw.trim();
  if (!input) return false;
  if (!/^https?:\/\//i.test(input)) input = `https://${input}`;
  try {
    const u = new URL(input);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname.toLowerCase().replace(/\.$/, "");
    return !!host && host.includes(".");
  } catch {
    return false;
  }
}

export function HeroWebsiteCheckBar() {
  const wc = useWebsiteCheck();
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [locked, setLocked] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;
    setLocked(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setLocked(false), 2000);

    if (!isValidCheckUrl(value)) {
      setError(true);
      return;
    }
    setError(false);
    const w = window as { gtag?: (...args: unknown[]) => void };
    w.gtag?.("event", "website_check_hero_submit", { location: "homepage_hero" });
    void wc.runCheck(value);
  };

  const checkStarted = wc.phase === "running" || wc.phase === "error" || wc.phase === "done";

  return (
    <div className="mt-12 w-full sm:mt-14">
      <div className="max-w-[620px]">
        <p className="mb-[10px] flex items-center gap-2 font-sans text-[12px] font-semibold uppercase tracking-[0.12em] text-[#f4c873]">
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#e8a93a]"
            style={{ boxShadow: "0 0 0 4px rgba(232,169,58,0.22)" }}
          />
          Free instant website check
        </p>
        <form onSubmit={onSubmit} noValidate>
          <div
            className="flex flex-col gap-[6px] rounded-[14px] bg-white p-[6px] md:flex-row md:items-center"
            style={{ boxShadow: "0 16px 40px rgba(0,0,0,0.35)" }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-[#8e9bb0]">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span aria-hidden="true" className="text-[16px] font-semibold text-[#5d6878]">https://</span>
              <label htmlFor="hero-website-check-url" className="sr-only">Your website address</label>
              <input
                id="hero-website-check-url"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="yourlawfirm.com"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-invalid={error || undefined}
                aria-describedby={error ? "hero-website-check-error" : "hero-website-check-note"}
                className="w-full min-w-0 flex-1 bg-transparent py-3 text-[16px] text-[#0f1b2d] placeholder:text-[#9aa3af] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={locked}
              className="inline-flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-[10px] px-5 py-[14px] text-[15px] font-bold text-[#0f1b2d] transition hover:-translate-y-px hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-80 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a93a] focus-visible:ring-offset-2 md:w-auto"
              style={{ background: "linear-gradient(180deg,#f2bb55,#e8a93a)" }}
            >
              Check my website <span aria-hidden="true">→</span>
            </button>
          </div>
        </form>
        {error && (
          <p id="hero-website-check-error" role="alert" className="mt-[10px] text-[13px] text-[#f87171]">
            {INVALID_MESSAGE}
          </p>
        )}
        <p id="hero-website-check-note" className="mt-[10px] text-[13px] leading-[1.5] text-[rgba(253,252,248,0.72)]">
          See how your site scores on <strong className="font-semibold text-white">speed, Core Web Vitals, security and key SEO tags</strong> in about 20 seconds. Free, no email needed.
        </p>
      </div>

      {checkStarted && (
        <div className="mt-8 w-full max-w-[720px]">
          {wc.phase === "running" && (
            <WebsiteCheckProgress url={wc.url} percent={wc.percent} elapsedSec={wc.elapsedSec} />
          )}
          {wc.phase === "error" && wc.error && (
            <WebsiteCheckErrorPanel errorType={wc.error} onTryAgain={wc.tryAgain} />
          )}
          {wc.phase === "done" && wc.result && (
            <WebsiteCheckResults
              result={wc.result}
              shownScore={wc.shownScore}
              expandedKey={wc.expandedKey}
              onToggleKey={wc.setExpandedKey}
              resultsNote={RESULTS_NOTE}
              primaryCtaLabel="Get my full free audit"
              secondaryLink={{ label: "See everything the full audit covers", href: "/law-firm-seo-audit" }}
              scrollTargetId="hero-form"
              onReset={wc.reset}
            />
          )}
        </div>
      )}
    </div>
  );
}
